/**
 * The reader's doors — the ways out of a page, and the questions that hold them.
 *
 * A page's questions are the *door out* of it (see `#/lib/document/quizGate`), and
 * every way of leaving a page comes through here: the bar's two presses, the wheel,
 * the keys, a flick, a jump from the thumbnail rail, a scroll that carries a page
 * into view, and the way out of the reader altogether. Keeping them in one place is
 * not tidiness — it is the thing that makes the rule un-walkable: a door that lived
 * with its control would be a door a *different* control could go round, and the
 * questions on the next page would be the one thing a reader could not reach by the
 * gesture they reach with.
 *
 * Three facts are held here, and each is state rather than a ref for the same
 * reason: something on screen is decided by it. `quizPage` is the page whose
 * questions are up; `settledQuiz` is the pages done with in this reading, which the
 * forward control's own label is decided from (a ref read during render is the thing
 * React Compiler will not reason about, and it cost this surface its compilation
 * once); `pendingPage` is where a held-up turn was going, which the card on screen
 * *names* — a reader stopped on page 2 of a five-page document they reached with End
 * is told they are going to page 5, and a promise the door then breaks is worse than
 * a door with nothing to say.
 *
 * What this hook does not own: the *page the reader is on* (`page`/`setPage` are the
 * reader's, because the scroll report writes it from outside this cluster), the
 * unsaved-page guard (`holdForDraft` arrives from `#/lib/document/useDraftHold` — the
 * asking is one rule, and a second copy of it here would be a second answer), and the
 * answer a finished quiz is reported with (`quizFinished`). All four arrive as
 * functions the caller owns and are erased where they enter, so that what a door is made
 * of is this file's own state and the page — never a fresh arrow's identity.
 *
 * And the role, which is the fifth thing it is told rather than works out: whether a page's
 * questions may hold a turn up at all (`asksOnTurn`). They may for a reader, who is being
 * carried somewhere, and they may not for the author walking the document they are writing —
 * whose own questions are raised by the page's Preview control instead (`onPreview`), which
 * is the one press this hook offers for them that is not a turn.
 */
import {useCallback, useEffect, useState} from 'react'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {
  type DocumentMaterial,
  type DocumentMaterialRef,
  type DocumentMaterialState,
  materialPageCounts,
  pageMaterialFor,
} from '#/lib/document/material'
import {
  carryOnPage,
  forwardStep,
  scrollStep,
  withQuizSettled,
} from '#/lib/document/quizGate'
import {useMaterialRequest} from '#/lib/document/useMaterialRequest'
import {useNonReactiveCallback} from '#/lib/hooks/useNonReactiveCallback'
import {type FlashBookPageResult} from '#/types/flashbook'

export function useQuizDoors({
  page,
  setPage: onSetPage,
  totalPages,
  paged,
  asksOnTurn,
  pageTable,
  material,
  materialState,
  carries,
  startQuizPage,
  onRequestMaterial,
  holdForDraft: onHoldForDraft,
  closeReader: onCloseReader,
  quizFinished: onQuizFinished,
}: {
  /** Where the reader is. Owned by the surface, which the scroll report writes. */
  page: number
  setPage: (next: number | ((current: number) => number)) => void
  totalPages: number
  /**
   * Whether the pages are the paged view's, which stands the forward door up.
   *
   * The forward *step* is the paged reader's: in the scroll, the scroll is the way
   * on and a page's questions are met by arriving at them, so the step and the
   * control that wears its label stand down together — one fact, passed in rather
   * than tested twice (`#/lib/document/quizGate`).
   */
  paged: boolean
  /**
   * Whether a turn is a *door* — false in the author's own reader.
   *
   * A page's questions are the way out of it so that nobody is carried past a page
   * they have not read; an author is not being carried anywhere, and the questions
   * they are walking away from are the ones they just wrote, so a turn that opened
   * them over the page being left would be the surface interrupting an author with
   * their own work. In that role the questions are raised by a press and only by a
   * press — the page's own bar wears the Preview control (`onPreview` in
   * `PdfFullscreen`, which is what `setQuizPage` here is for) — and every walk below
   * behaves as though no page asked anything.
   */
  asksOnTurn: boolean
  /** What each page holds, from the post, so a turn needs no fetch. */
  pageTable?: DocumentMaterialRef
  /** The material itself, when it has arrived. */
  material?: DocumentMaterial
  materialState: DocumentMaterialState
  /** Whether the document asks anything anywhere. */
  carries: boolean
  /**
   * A page whose questions are open *on arrival* rather than behind the turn.
   *
   * The author's path, and only ever that: the composer's page bar previews the
   * questions that were just written, and there is nothing to read before them.
   */
  startQuizPage?: number
  /** The caller's ask for material — read on the reader's own facts. */
  onRequestMaterial?: () => void
  /** The unsaved-page guard, from `#/lib/document/useDraftHold`. */
  holdForDraft: (deed: () => void) => boolean
  /** The way out of the reader. */
  closeReader: () => void
  /** A finished quiz's result. */
  quizFinished: (result: {
    pageResults: FlashBookPageResult
    score: number
    maxPoints: number
  }) => void
}) {
  const {_} = useLingui()

  /*
   * The caller's own functions, erased where they enter.
   *
   * Every one of these arrives with an identity its caller chose, and each is named in
   * a dependency list below — the page setter, the unsaved-page guard, the way out, and
   * the report a finished quiz is handed. The guard is the one that really churns: it
   * is a `useCallback` over the open editor and its dirtiness (`#/lib/document/useDraftHold`),
   * so it is re-made every time the author types or opens a page, and a door that
   * depended on that identity would be re-made with it — the reader's renders and its
   * key listener becoming the card's business rather than the reader's. Erasing here
   * rather than at the call site is deliberate: this hook is the unit that decides what a
   * door is, so it is the unit that must not be told a new one on every keystroke
   * (`useNonReactiveCallback`; the rule that keeps this true is
   * `bsky-internal/no-reactive-prop-deps`). The value each call reads is still the
   * latest the caller handed over.
   */
  const setPage = useNonReactiveCallback(onSetPage)
  const holdForDraft = useNonReactiveCallback(onHoldForDraft)
  const closeReader = useNonReactiveCallback(onCloseReader)
  const quizFinished = useNonReactiveCallback(onQuizFinished)

  /**
   * The questions on screen, if any — the door between two pages.
   *
   * Asked with the blob, not the material: a page table is on the post, so a turn
   * can decide to ask something without waiting for a fetch, and the card itself
   * waits for the questions it needs (see below).
   */
  const [quizPage, setQuizPage] = useState<number | null>(startQuizPage ?? null)

  /**
   * Pages whose questions are done with in this reading, answered or stepped over.
   *
   * State rather than a ref, and the reason is that the reader's *output* is decided
   * from it: the forward door is `forwardStep({… settled})` computed while rendering
   * (`forward` below), and the label the forward control wears says which of the two
   * doors it is. A set that decides what is drawn is a fact the render has to see, and
   * a ref read during render is the one thing React Compiler refuses to reason about —
   * which is why this component, the surface the whole feature is, was being left
   * uncompiled. The write costs no render of its own: every one of them is in the same
   * batch as the page state it belongs to. And nothing decides a turn from a value it
   * has just written, because every walk that consults this set (a step forward, a
   * flick, a jump, the carry-on after a door) begins at the page *after* the one being
   * settled.
   */
  const [settledQuiz, setSettledQuiz] = useState<ReadonlySet<number>>(
    () => new Set(),
  )

  const questionsOnPage = useCallback(
    (target: number) => {
      if (pageTable) return materialPageCounts(pageTable, target).questions
      return material
        ? (pageMaterialFor(material, target)?.questions.length ?? 0)
        : 0
    },
    [material, pageTable],
  )

  /**
   * What each page asks, as a *turn* sees it — nothing at all while the author is writing.
   *
   * Nought is the honest answer rather than a special case at each door: every walk below
   * asks this instead of `questionsOnPage`, so nothing has to remember to check the role,
   * and the forward control's own label follows for free — `forward` finds no page to ask
   * about, so it says *Next page* instead of *Answer to carry on*.
   *
   * What it deliberately does not touch: `quizQuestions` and the not-a-door effect, which
   * are about a card that is *already* up. An author who pressed Preview gets the same card
   * a reader does, and closing it ends the same way (the walk after a door), because a
   * second rule for the same card is a second card to keep working.
   */
  const questionsOnTurn = useCallback(
    (target: number) => (asksOnTurn ? questionsOnPage(target) : 0),
    [asksOnTurn, questionsOnPage],
  )

  /**
   * The questions the open quiz is waiting for, if it has them yet.
   *
   * Somebody who opened this reader *straight into* a page's questions (the
   * composer's preview) never asked the carousel's fetch to start — nothing may
   * have asked for a document's material at all until now — so the card is on
   * screen waiting for questions that nobody has gone to get. Reaching a page by
   * turning asks for the material on the way past (`stepForward`); arriving
   * straight at one has to ask here.
   */
  const quizQuestions =
    quizPage === null
      ? []
      : (pageMaterialFor(material, quizPage)?.questions ?? [])

  /*
   * The material, asked for on the reader's own facts — the open, and a card that is
   * already up on arrival — rather than on the caller's function, which is a new arrow
   * on every render of the card that owns this reader. The whole of it, including why
   * that distinction is the difference between one fetch and one per render, is
   * `#/lib/document/useMaterialRequest`.
   *
   * The asker is used by the doors below as well as by the two moments above: a turn or
   * a scroll that walks into a page's questions fetches them on the spot, and one
   * identity for the request keeps those callbacks stable too.
   */
  const askForMaterial = useMaterialRequest({
    carries,
    state: materialState,
    quizPage,
    request: onRequestMaterial,
  })

  /**
   * A quiz the record cannot answer is not a door.
   *
   * Counts come off the post and the questions come from the record, which can
   * disagree — a page's material edited away, a record written by an older build,
   * or a record that is simply not there any more — and a card over a page whose
   * questions cannot be asked has no way to be finished with. The card is dropped
   * rather than left spinning, and the page is free to turn.
   *
   * Waiting is the one thing that is not that: while the record is in flight the
   * card stands, because the questions are *coming*. `none` is the state where the
   * record has answered — and the answer was nothing — which is why the reader is
   * told it apart from a fetch that has not landed yet.
   *
   * A *failed* read is the other one, and it is the reason this effect names its
   * states rather than asking for questions and giving up: the record did not
   * answer, so nothing is known about what is on the page, and a reader dropped
   * past their own door for a minute of bad wire would have lost the questions for
   * the rest of the session (the fetch is read once per document). So the card
   * stays where it is, drawing the failure and the press that asks again — the
   * door is held, not opened, until the reader has been told and has chosen.
   */
  useEffect(() => {
    if (quizPage === null) return
    if (
      materialState === 'asking' ||
      materialState === 'unasked' ||
      materialState === 'failed'
    ) {
      return
    }
    if ((pageMaterialFor(material, quizPage)?.questions.length ?? 0) > 0) return
    /*
     * A page is only *found* empty when the material lands, which is after render, so this is
     * an effect's write and `react-hooks/set-state-in-effect` warns about it — left standing,
     * because silencing it would cost the reader its compilation: the compiler skips any
     * component holding a disabled React rule (`React Compiler has skipped optimizing this
     * component because one or more React ESLint rules were disabled`). The write has to be
     * state all the same: what it records decides what the reader draws.
     */
    setSettledQuiz(settled => withQuizSettled(settled, quizPage))
    setQuizPage(null)
  }, [material, materialState, quizPage])

  const goToPage = useCallback(
    (next: number) => {
      setPage(current => {
        const clamped = Math.min(Math.max(next, 1), totalPages)
        return clamped === current ? current : clamped
      })
    },
    [setPage, totalPages],
  )

  /**
   * Where a held-up turn was going, so settling the questions can carry on to it.
   *
   * Set by the scroll's own door, and by a jump: a reader who flicked to page 5 — or
   * clicked page 5 in the rail, or pressed End — and was stopped at a page that asks
   * on the way has already said where they want to be, and answering should take them
   * there rather than to the page after the one they answered. Null while nothing is
   * being held up.
   *
   * State rather than a ref, because the card on screen *names* it: a reader held up
   * on page 2 of a five-page document they reached with End is told they are going to
   * page 5, not to page 3, and a promise the door then breaks is worse than a door
   * with nothing to say. Every write is paired with the card's own state change, so it
   * costs no extra render.
   */
  const [pendingPage, setPendingPage] = useState<number | null>(null)

  /**
   * A page asked for by name: the thumbnails, Home and End.
   *
   * Asked in the terms of the *landing* rather than the step, because a jump is a
   * scroll with a destination: which page it stops at is decided by what it crosses,
   * so the rail's page 9 and End are `scrollStep` asked once with the page the reader
   * meant. It stops at the first page that asks and is not done with, remembers where
   * the reader was heading (`pendingPage`), and carries them on once it is answered —
   * so no jump can walk around a page whose questions are unanswered, and a jump over
   * two of them is stopped twice rather than passed once.
   *
   * Backwards crosses nothing — the rule walks forward — so re-reading is never held
   * up, which is the one thing a page's questions must never do.
   */
  const jumpToPage = useCallback(
    (next: number): boolean => {
      /*
       * The questions own the surface while they are up, so nothing moves the page out
       * from under the card — not the rail, not the keys, not the arrows. Every one of
       * those comes through here, which is why the rule is here rather than in each of
       * them.
       */
      if (quizPage !== null) return false
      const destination = Math.min(Math.max(next, 1), totalPages)
      const step = scrollStep({
        from: page,
        to: destination,
        totalPages,
        questionsOn: questionsOnTurn,
        settled: target => settledQuiz.has(target),
      })
      if ('askPage' in step) {
        askForMaterial()
        if (step.askPage !== page) goToPage(step.askPage)
        setPendingPage(destination)
        setQuizPage(step.askPage)
        return step.askPage !== page
      }
      goToPage(destination)
      return destination !== page
    },
    [
      askForMaterial,
      goToPage,
      page,
      questionsOnTurn,
      quizPage,
      settledQuiz,
      totalPages,
    ],
  )

  /**
   * A page asked for by hand: the arrow back, and the keys that move one page.
   *
   * One step is a jump that crosses nothing forward, so this is `jumpToPage` with the
   * card's own draft asked about first: the author's words are the other thing a turn
   * can throw away, and they are asked about *before* the door rather than inside it.
   */
  const requestPage = useCallback(
    (next: number): boolean => {
      if (holdForDraft(() => jumpToPage(next))) return false
      return jumpToPage(next)
    },
    [holdForDraft, jumpToPage],
  )

  /** One page back, which is a page already read: never held up by anything. */
  const stepBack = useCallback(
    (): boolean => requestPage(page - 1),
    [page, requestPage],
  )

  /**
   * The way out of the reader, which is the last door an unsaved page can hold up.
   *
   * Escape and the header's own control both come through here, so every exit asks
   * once, however it was taken.
   */
  const requestClose = useCallback(() => {
    if (holdForDraft(closeReader)) return
    closeReader()
  }, [closeReader, holdForDraft])

  /**
   * A step *forward*, which is the one a page's questions can hold up.
   *
   * Every way of moving on goes through here — the bar's forward press, and
   * PageDown with it — so the rule lives in one place and no door can be walked
   * around by using a different control.
   */
  const stepForwardNow = useCallback((): boolean => {
    if (quizPage !== null) return false
    const step = forwardStep({
      from: page,
      totalPages,
      questionsOn: questionsOnTurn,
      settled: target => settledQuiz.has(target),
    })
    if ('askPage' in step) {
      askForMaterial()
      setQuizPage(step.askPage)
      return false
    }
    if (step.toPage !== undefined) {
      goToPage(step.toPage)
      return true
    }
    return false
  }, [
    askForMaterial,
    goToPage,
    page,
    questionsOnTurn,
    quizPage,
    settledQuiz,
    totalPages,
  ])

  /**
   * The step, offered only to a page that has nothing unsaved on it.
   *
   * The draft is asked about *before* the door rather than inside it: a page that
   * asks something would otherwise open its questions over the card the author is
   * writing on, and the questions would be answered — or the turn taken — while the
   * card was still holding words nobody had confirmed.
   */
  const stepForward = useCallback((): boolean => {
    if (holdForDraft(stepForwardNow)) return false
    return stepForwardNow()
  }, [holdForDraft, stepForwardNow])

  /**
   * The door a scroll walks into, which is the paged door widened.
   *
   * Scrolling reports the page it has brought into view (`ScrollReader` measures it,
   * and says whether the reader has also reached the document's own end), and this
   * decides what that is allowed to mean. The rule itself is in `scrollStep`, beside
   * the paged one, because it is the same rule — what belongs here is only what the
   * reader has to *do* about the answer:
   *
   *  - **A held-up turn remembers where it was going** (`pendingPage`), so that
   *    answering carries the reader on to the page they were heading for, rather than
   *    to the page after the one they answered.
   *  - **The page being asked about is brought into view.** A flick is what caused the
   *    asking, so the view is usually already past the page it is being asked about;
   *    `pinnedPage` pulls it back.
   *  - **The questions own the surface while they are open.** A scroll behind the card
   *    cannot change which page is being asked about.
   */
  const onScrollPageNow = useCallback(
    (next: number, atEnd: boolean) => {
      if (quizPage !== null) return
      const step = scrollStep({
        from: page,
        /*
         * Pushing against the bottom of the last page is leaving that page: there is
         * no page to arrive at, and the way out of a page is its questions.
         */
        to: atEnd && next === totalPages ? next + 1 : next,
        totalPages,
        questionsOn: questionsOnTurn,
        settled: target => settledQuiz.has(target),
      })
      if ('askPage' in step) {
        askForMaterial()
        if (step.askPage !== page) goToPage(step.askPage)
        setPendingPage(next)
        setQuizPage(step.askPage)
        return
      }
      /*
       * Where the reader lands: the page the door named, or — when it names none,
       * which is only ever a flick pushing past the last page — the page the view is
       * already showing.
       *
       * That second case is a reader who has scrolled to the bottom of the last page.
       * There is no page to arrive at, so the door has nothing to say, but the reader
       * is *on* the last page and the counter has to say so: leaving it on the page
       * before, while the last page's own questions are being asked of them, is the
       * reader's position denied to them.
       */
      goToPage(step.toPage ?? next)
    },
    [
      askForMaterial,
      goToPage,
      page,
      questionsOnTurn,
      quizPage,
      settledQuiz,
      totalPages,
    ],
  )

  /**
   * The scroll's door, offered only to a page that has nothing unsaved on it.
   *
   * A scroll the card is holding up is reported again as the wheel or the fingers
   * keep going, and each report overwrites the last — so the deed that is finally
   * taken is the one the reader's hands asked for most recently, not whichever one
   * happened to arrive first. Note what waiting costs: the scroll keeps moving
   * while the page does not, so the view runs a little ahead of the page number in
   * the header until the reader answers. The alternative is the card unmounting
   * under words nobody confirmed, which is the thing being asked about.
   */
  const onScrollPage = useCallback(
    (next: number, atEnd: boolean) => {
      if (holdForDraft(() => onScrollPageNow(next, atEnd))) return
      onScrollPageNow(next, atEnd)
    },
    [holdForDraft, onScrollPageNow],
  )

  /**
   * Done with a page's questions: settled, and on to the page after it.
   *
   * Answering and skipping end the same way, and that is deliberate — a question
   * nobody can answer must not be a document nobody can leave. What differs is
   * the result the caller is handed, which is nothing when the reader skipped.
   */
  const closeQuiz = useCallback(
    (result?: {
      pageResults: FlashBookPageResult
      score: number
      maxPoints: number
    }) => {
      if (quizPage === null) return
      setSettledQuiz(settled => withQuizSettled(settled, quizPage))
      setQuizPage(null)
      if (result) quizFinished(result)
      /*
       * A held-up turn carries on to where it was going — stopping again if the way
       * there crosses another page that asks, so a flick (or a jump) over two pages
       * that ask is answered twice rather than passed once. Everything else takes the
       * paged step: the page after the door.
       */
      const pending = pendingPage
      if (pending !== null) {
        setPendingPage(null)
        const step = scrollStep({
          from: quizPage + 1,
          to: pending,
          totalPages,
          questionsOn: questionsOnTurn,
          /*
           * `settledQuiz` here is the *render's* copy, which does not yet hold the page
           * this door is closing — and the walk begins at the page after it, so it never
           * asks. Anything else would be deciding a turn from a value just written by
           * hand, which is what the ref did and what the state cannot.
           */
          settled: target => settledQuiz.has(target),
        })
        if ('askPage' in step) {
          askForMaterial()
          goToPage(step.askPage)
          setPendingPage(pending)
          setQuizPage(step.askPage)
          return
        }
        goToPage(step.toPage ?? pending)
        return
      }
      // The page that follows is read *after* the door, which is the whole point
      // of asking here rather than on arrival.
      if (quizPage < totalPages) goToPage(quizPage + 1)
    },
    [
      askForMaterial,
      goToPage,
      pendingPage,
      questionsOnTurn,
      quizFinished,
      quizPage,
      settledQuiz,
      totalPages,
    ],
  )

  /**
   * The forward control is live wherever there is somewhere to go *or* something
   * to ask — including the last page, whose questions are the door out.
   */
  const forward = forwardStep({
    from: page,
    totalPages,
    questionsOn: questionsOnTurn,
    settled: target => settledQuiz.has(target),
  })
  const canStepForward =
    quizPage === null &&
    ('askPage' in forward || forward.toPage !== undefined) &&
    paged
  /**
   * The label a forward control wears, which is the door when there is one.
   *
   * Every control that goes forward says the same thing about the same step — the
   * bar's forward press, and the wheel and the keys, which are the same step by
   * other means — so a reader who has to answer before carrying on is told so
   * whichever one they reach for.
   */
  const forwardLabel =
    'askPage' in forward && quizPage === null
      ? _(msg`Answer to carry on`)
      : _(msg`Next page`)

  /**
   * The page the questions card says the reader is carried on to, if any: the page
   * they meant when the door was opened rather than the one next door, so the card
   * cannot promise a page the door is not going to (`carryOnPage` in the gate).
   */
  const carriedOnTo = carryOnPage({quizPage, pendingPage, totalPages})

  return {
    quizPage,
    setQuizPage,
    settledQuiz,
    quizQuestions,
    askForMaterial,
    requestPage,
    stepBack,
    stepForward,
    onScrollPage,
    jumpToPage,
    closeQuiz,
    requestClose,
    canStepForward,
    forwardLabel,
    carriedOnTo,
  }
}
